/**
 * API service for communicating with the backend.
 * Uses Axios for HTTP requests.
 * Inspired by DBeaver's client-server communication.
 */

import axios, { AxiosInstance } from 'axios';
import { Connection, QueryRequest, QueryResult, LoginResponse, TableInfo, NaturalLanguageToSqlRequest, ExplainSqlRequest, OptimizeSqlRequest, AiResponse, SqlTab } from '../types';
import { getToken } from './storage';

// Use relative URL in production (nginx will proxy /api to backend)
// Use absolute URL in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8080/api');

// Create Axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired, invalid, or forbidden - redirect to login
      // Clear token from storage
      localStorage.removeItem('sql_client_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authApi = {
  login: async (password: string): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', { password });
    return response.data;
  },

  validateToken: async (): Promise<boolean> => {
    try {
      const response = await apiClient.get<boolean>('/auth/validate');
      return response.data;
    } catch {
      return false;
    }
  },
};

// Connection API
export const connectionApi = {
  getAll: async (): Promise<Connection[]> => {
    const response = await apiClient.get<Connection[]>('/connections');
    return response.data;
  },

  getById: async (id: string): Promise<Connection> => {
    const response = await apiClient.get<Connection>(`/connections/${id}`);
    return response.data;
  },

  create: async (connection: Connection): Promise<Connection> => {
    const response = await apiClient.post<Connection>('/connections', connection);
    return response.data;
  },

  update: async (id: string, connection: Connection): Promise<Connection> => {
    const response = await apiClient.put<Connection>(`/connections/${id}`, connection);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/connections/${id}`);
  },

  test: async (connection: Connection): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/connections/test', connection);
    return response.data;
  },

  close: async (id: string): Promise<void> => {
    await apiClient.post(`/connections/${id}/close`);
  },
};

// Query API
export const queryApi = {
  execute: async (request: QueryRequest): Promise<QueryResult> => {
    const response = await apiClient.post<QueryResult>('/query/execute', request);
    return response.data;
  },

  executeMultiple: async (request: QueryRequest): Promise<QueryResult[]> => {
    const response = await apiClient.post<QueryResult[]>('/query/execute-multiple', request);
    return response.data;
  },
};

// Schema API
export const schemaApi = {
  getSchemas: async (connectionId: string): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`/schema/${connectionId}/schemas`);
    return response.data;
  },

  getTables: async (connectionId: string): Promise<TableInfo[]> => {
    const response = await apiClient.get<TableInfo[]>(`/schema/${connectionId}/tables`);
    return response.data;
  },

  getTableStructure: async (connectionId: string, tableName: string): Promise<any> => {
    const response = await apiClient.get(`/schema/${connectionId}/table/${tableName}`);
    return response.data;
  },

  getDatabaseInfo: async (connectionId: string): Promise<Record<string, string>> => {
    const response = await apiClient.get(`/schema/${connectionId}/info`);
    return response.data;
  },
};

// Export API
export const exportApi = {
  toCsv: async (columns: string[], rows: Record<string, any>[]): Promise<string> => {
    const response = await apiClient.post<string>('/export/csv', { columns, rows });
    return response.data;
  },

  toJson: async (rows: Record<string, any>[]): Promise<string> => {
    const response = await apiClient.post<string>('/export/json', { rows });
    return response.data;
  },
};

// AI API
export const aiApi = {
  naturalLanguageToSql: async (connectionId: string, naturalLanguage: string): Promise<AiResponse> => {
    const request: NaturalLanguageToSqlRequest = { connectionId, naturalLanguage };
    const response = await apiClient.post<AiResponse>('/ai/natural-language-to-sql', request);
    return response.data;
  },

  explainSql: async (connectionId: string, sql: string): Promise<AiResponse> => {
    const request: ExplainSqlRequest = { connectionId, sql };
    const response = await apiClient.post<AiResponse>('/ai/explain-sql', request);
    return response.data;
  },

  optimizeSql: async (connectionId: string, sql: string): Promise<AiResponse> => {
    const request: OptimizeSqlRequest = { connectionId, sql };
    const response = await apiClient.post<AiResponse>('/ai/optimize-sql', request);
    return response.data;
  },
};

// Tabs API
export const tabsApi = {
  getAll: async (): Promise<SqlTab[]> => {
    const response = await apiClient.get<SqlTab[]>('/tabs');
    return response.data;
  },

  getById: async (id: string): Promise<SqlTab> => {
    const response = await apiClient.get<SqlTab>(`/tabs/${id}`);
    return response.data;
  },

  saveAll: async (tabs: SqlTab[]): Promise<SqlTab[]> => {
    const response = await apiClient.post<SqlTab[]>('/tabs', tabs);
    return response.data;
  },

  update: async (id: string, tab: SqlTab): Promise<SqlTab> => {
    const response = await apiClient.put<SqlTab>(`/tabs/${id}`, tab);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/tabs/${id}`);
  },
};

export default apiClient;

